import { Box, styled } from '@planne-software/uni/mui/material';
import { DatePicker } from '@planne-software/uni/DatePicker';
import { SelectPopover } from '@planne-software/uni/SelectPopover';
import {
  dateCalendarClasses,
  dayCalendarClasses,
  monthCalendarClasses,
  pickersFadeTransitionGroupClasses,
  pickersLayoutClasses,
  pickersSlideTransitionClasses,
  yearCalendarClasses,
} from '@mui/x-date-pickers';
import {
  CELL_WIDTH,
  COLUMN_GAP,
  GRID_WIDTH,
  ROW_GAP,
  SLIDE_MIN_HEIGHT,
} from '@src/domains/scheduling/components/constants';

export const StyledDatePicker = styled(DatePicker)(({ theme }) => ({
  width: '100%',
  maxWidth: 'none',
  margin: 0,
  [`& .${pickersLayoutClasses.root}`]: { width: '100%', minWidth: 0 },
  [`& .${pickersLayoutClasses.contentWrapper}`]: { width: '100%', minWidth: 0 },
  [`& .${dateCalendarClasses.root}`]: {
    width: '100%',
    height: 'auto',
    maxHeight: 'unset',
    overflow: 'visible',
    margin: 0,
  },
  [`& .${dayCalendarClasses.header}, & .${dayCalendarClasses.weekContainer}`]: {
    display: 'flex',
    justifyContent: 'flex-start',
    gap: `${COLUMN_GAP}px`,
    width: `${GRID_WIDTH}px`,
    margin: 0,
  },
  [`& .${dayCalendarClasses.header}`]: { marginBottom: `${ROW_GAP}px` },
  [`& .${dayCalendarClasses.weekContainer}`]: {
    marginBottom: `${ROW_GAP}px`,
    '&:last-of-type': { marginBottom: 0 },
  },
  [`& .${dayCalendarClasses.monthContainer}`]: {
    width: `${GRID_WIDTH}px`,
    overflow: 'visible',
    height: 'auto',
  },
  [`& .${pickersSlideTransitionClasses.root}`]: {
    overflow: 'visible',
    minHeight: `${SLIDE_MIN_HEIGHT}px`,
  },
  [`& .${pickersFadeTransitionGroupClasses.root}`]: {
    display: 'grid',
    overflow: 'visible',
    '& > *': {
      gridColumn: 1,
      gridRow: 1,
    },
  },
  [`& .${monthCalendarClasses.root}`]: {
    width: '100%',
    boxSizing: 'border-box',
    padding: 0,
    rowGap: '1rem',
    columnGap: 0,
    justifyContent: 'space-between',
  },
  [`& .${monthCalendarClasses.button}`]: {
    flex: '0 0 auto',
    flexBasis: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  [`& .${yearCalendarClasses.root}`]: {
    width: '100%',
    boxSizing: 'border-box',
    padding: 0,
    rowGap: '1rem',
    justifyContent: 'space-between',
  },
  [`& .${dayCalendarClasses.weekDayLabel}`]: {
    ...theme.tokens.typography['text-md-medium'],
    color: theme.tokens.colors['text-color-neutral-strongest'],
    width: CELL_WIDTH,
    height: 24,
    margin: 0,
    flexShrink: 0,
    textAlign: 'center',
  },
}));

// ---------- Container ----------

export const ContentBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile: boolean }>(({ isMobile }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  padding: isMobile ? '1rem' : '1.5rem',
}));

export const FooterColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  width: '100%',
});

export const TotalRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  width: '100%',
  color: theme.tokens.colors['text-color-neutral-strongest'],
}));

export const TotalLabel = styled('span')(({ theme }) => ({
  ...theme.tokens.typography['text-md-medium'],
}));

export const TotalValue = styled('span')(({ theme }) => ({
  ...theme.tokens.typography['display-sm-bold'],
}));
